/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import ClinicLogo from '../../assets/SVG/img_logo_svg.svg';
import { useVerboseLog } from '../../utils/debug';
import { toTitleCase } from '../../utils/commonUtils';
import { SemenAnalysisReportResponse } from '../SemanAnalysisComponent/SemanAnalysisComponentHelper';

interface SemenAnalysisProps {
  data: SemenAnalysisReportResponse;
  iframeRef?: React.RefObject<HTMLIFrameElement>;
}

const SemenAnalysisReport: React.FC<SemenAnalysisProps> = ({ data, iframeRef }) => {
  const verboseLog = useVerboseLog();
  verboseLog('data', data);
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  const html = `<!DOCTYPE html>
    <html>
    <head>
    <meta charset="UTF-8">
    <style>
      body {
        font-family: Arial, sans-serif;
        padding: 40px;
        width: 794px;
        height: 1123px;
      }

      .header {
        display: flex;
        justify-content: space-between;
        border-bottom: 2px solid #ccc;
        padding-bottom: 20px;
      }

      .info {
        background-color: #E1E1E1;
        padding: 16px;
        border-radius: 12px;
        max-width: 60%;
      }

      .info h1 {
        margin: 0;
        font-size: 28px;
      }

      .info p {
        margin: 4px 0;
        font-size: 14px;
      }

      .logo img {
        max-width: 196px;
      }

      h3 {
        margin-top: 30px;
        font-size: 20px;
        border-bottom: 1px solid #ccc;
        padding-bottom: 4px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 10px;
      }

      th, td {
        border: 1px solid #000;
        padding: 6px;
        text-align: left;
      }

      th {
        background-color: #f0f0f0;
      }

      .end_report {
        margin-top: 40px;
        text-align: center;
        font-style: italic;
      }
    </style>
    </head>

    <body>
      <div class="header">
        <div class="info">
          <h1>PureCare Clinic</h1>
          <p>756 University Avenue, Lake Jeniferberg 35967</p>
          <p><strong>Email:</strong> ramon_morissette54@gmail.com</p>
          <p><strong>Mob:</strong> 578-778-3291 / 578-778-3291</p>
          <p><strong>Website:</strong> purecareclinic.com</p>
        </div>
        <div class="logo">
          <img src=${ClinicLogo} alt="Clinic Logo" />
        </div>
      </div>

      <h3>Semen Analysis Report</h3>
      <p><strong>Doctor:</strong> Dr. ${data.doctor_name ?? ''}</p>
      <p><strong>Service Name:</strong> ${toTitleCase(data.service_name)}</p>
      <p><strong>Sample Number:</strong> ${data.sample_number}</p>
      <p><strong>Date of Prescription:</strong> ${formatDate(data.date_of_prescription)}</p>

      <table>
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          ${[
    ['Collection Date', formatDate(data.collection_date)],
    ['Collection Time', data.collection_time ?? 'N/A'],
    ['Receiving Date', formatDate(data.receiving_date)],
    ['Receiving Time', data.receiving_time ?? 'N/A'],
    ['Volume', data.volume],
    ['Color', data.color],
    ['Appearance', data.appearance],
    ['pH', data.ph],
    ['Liquefaction Time (min)', data.liquefaction_time_in_min],
    ['Viscosity', data.viscosity],
    ['Odour', data.odour],
    ['Sperm Concentration', data.sperm_concentration],
    ['Total Sperm Count', data.total_sperm_count],
    ['Rapid Progressive', data.rapid_progressive],
    ['Slow Progressive', data.slow_progressive],
    ['Non Progressive', data.non_progressive],
    ['Viability', data.viability],
    ['Pus Cells', data.pus_cells],
    ['RBC', data.rbc],
    ['Epithelial Cells', data.epithelial_cells],
    ['Interpretation', data.interpretation ?? 'N/A'],
    ['Remark', data.remark ?? data.interpretation_remark ?? 'N/A'],
  ]
    .filter(([, value]) => value !== null)
    .map(
      ([label, value]) =>
        `<tr><td><strong>${label}</strong></td><td>${value ?? 'N/A'}</td></tr>`
    )
    .join('')}
        </tbody>
      </table>

      <div class="end_report">
        <strong>End of Report</strong>
        <p>Note: This report is generated for informational purposes only and should not be considered as medical advice.</p>
      </div>
    </body>
    </html>`;

  return (
    data && (
      <iframe
        title="SemenAnalysisReport"
        width="100%"
        height="100%"
        style={{ border: 'none', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', borderRadius: '8px' }}
        srcDoc={html}
        ref={iframeRef}
      />
    )
  );
};

export default SemenAnalysisReport;
